import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { money } from './DashCard';

const Delta = ({ pct, testid }) => {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-0.5 text-sm text-slate-500 dark:text-slate-300" data-testid={testid}>
        <Minus className="h-3 w-3" /> no data last week
      </span>
    );
  }
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-sm font-semibold ${up ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-300'}`}
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(pct)}% <span className="font-normal text-slate-500 dark:text-slate-300 ml-0.5">vs last wk</span>
    </span>
  );
};

const Kpi = ({ label, value, accent, footer, to, delay, testid }) => (
  <Link
    to={to}
    className="dash-rise group surface relative block overflow-hidden rounded-2xl px-5 py-5 hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={testid}
  >
    <span className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full" style={{ background: accent }} />
    <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">{label}</p>
    <p className="mt-2 text-[clamp(1.05rem,1.55vw,1.7rem)] font-mono font-semibold tracking-tight text-slate-900 dark:text-white whitespace-nowrap" title={String(value)} data-testid={`${testid}-value`}>
      {value}
    </p>
    <div className="mt-1.5">{footer}</div>
  </Link>
);

export const StatTile = ({ label, value, accent, footer, tone = 'default', delay = 0, testid, className = '' }) => (
  <div
    className={`dash-rise relative overflow-hidden rounded-2xl px-5 py-5 ${className} ${tone === 'primary' ? 'surface-primary' : 'surface'}`}
    style={{ animationDelay: `${delay}ms` }}
    data-testid={testid}
  >
    {tone !== 'primary' && <span className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full" style={{ background: accent }} />}
    <p className={`text-sm font-semibold ${tone === 'primary' ? 'text-blue-900/80 dark:text-blue-100' : 'text-slate-600 dark:text-slate-300'}`}>{label}</p>
    <p className={`mt-2 text-[clamp(1.05rem,1.55vw,1.7rem)] font-mono font-semibold tracking-tight whitespace-nowrap ${tone === 'primary' ? 'text-[#0f2a5c] dark:text-white' : tone === 'negative' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'}`} title={String(value)} data-testid={testid ? `${testid}-value` : undefined}>
      {value}
    </p>
    {footer && <div className={`mt-1.5 text-sm ${tone === 'primary' ? 'text-blue-900/70 dark:text-blue-100' : 'text-slate-500 dark:text-slate-300'}`}>{footer}</div>}
  </div>
);

export const KpiStrip = ({ kpis }) => {
  if (!kpis) return null;
  const items = [
    { key: 'gross', label: 'Week Gross', value: money(kpis.gross.value), accent: '#1e3a8a', to: '/summary', pct: kpis.gross.change_pct },
    { key: 'net', label: 'Net to Carriers', value: money(kpis.net.value), accent: '#1d4ed8', to: '/summary', pct: kpis.net.change_pct },
    { key: 'fees', label: 'Dispatch Fees Earned', value: money(kpis.fees.value), accent: '#3b82f6', to: '/accounting', pct: kpis.fees.change_pct },
    { key: 'loads', label: 'Loads This Week', value: kpis.loads.value, accent: '#60a5fa', to: '/loads', pct: kpis.loads.change_pct },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-4" data-testid="dashboard-kpi-strip">
      {items.map((k, i) => (
        <Kpi
          key={k.key}
          label={k.label}
          value={k.value}
          accent={k.accent}
          to={k.to}
          delay={i * 60}
          testid={`kpi-${k.key}`}
          footer={<Delta pct={k.pct} testid={`kpi-${k.key}-delta`} />}
        />
      ))}
      <Kpi
        label="Active Units"
        value={kpis.active_units.value}
        accent="#334155"
        to="/units"
        delay={240}
        testid="kpi-active-units"
        footer={<span className="text-sm text-slate-500 dark:text-slate-300">of {kpis.active_units.total} total</span>}
      />
      <Kpi
        label="Active Drivers"
        value={kpis.active_drivers.value}
        accent="#2563eb"
        to="/drivers"
        delay={300}
        testid="kpi-active-drivers"
        footer={<span className="text-sm text-slate-500 dark:text-slate-300">on the roster</span>}
      />
    </div>
  );
};
